/* eslint-disable jsx-a11y/click-events-have-key-events */
import Copy16 from '@carbon/icons-react/es/copy/16';
import { Typography } from 'antd';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  MoreActionsWrapper,
  TemplateCard,
  TemplateCardIconWrapper,
} from 'components/atoms/card/template-card';
import { Menu as DropdownMenu } from 'components/atoms/dropdown/menu';
import { IconShell } from 'components/atoms/icon-shell';
import { confirm } from 'components/atoms/modal';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { TemplateObjectType } from 'data/templates/api';
import { Translate } from 'hooks/use-translation';
import { FormTemplateIcon, Loading } from './styles';
import { TemplateListProp } from './types';

const { Paragraph } = Typography;

export const RenderFormTemplateCard = ({
  template,
  isLoading,
  translate,
  onDelete,
  onDuplicate,
}: {
  template: TemplateListProp;
  isLoading?: boolean;
  translate: Translate;
  onDelete?: (id, objectType: TemplateObjectType) => void;
  onDuplicate?: (id, objectType: TemplateObjectType) => void;
}): JSX.Element => {
  const deleteConfirmation = () => {
    confirm({
      okText: translate('delete'),
      okType: 'danger',
      cancelText: translate('cancel'),
      onCancel: () => {},
      title: translate('template.delete.confirm.title'),
      content: (
        <>
          {template.usageCount && template.usageCount > 0 ? (
            <>
              <div>
                {translate('form_template.delete.heading', {
                  usageCount: template.usageCount,
                  strong: (chunks) => <strong>{chunks}</strong>,
                })}
              </div>
              <div>{translate('form_template.delete.subheading')}</div>
              <ul>
                <li>{translate('form_template.delete.subheading_message1')}</li>
                <li>{translate('form_template.delete.subheading_message2')}</li>
              </ul>
              <div>{translate('form_template.delete_confirmation')}</div>
            </>
          ) : (
            <div>{translate('template.delete.confirm.message')}</div>
          )}
        </>
      ),
      onOk: async () => {
        await onDelete?.(template.templateId, TemplateObjectType.Form);
      },
    });
  };

  const duplicateHandler = useCallback(() => {
    onDuplicate?.(template.templateId, TemplateObjectType.Form);
  }, [onDuplicate, template.templateId]);

  return (
    <TemplateCard data-cy="card" private={template.private}>
      {isLoading ? (
        <Loading />
      ) : (
        <TemplateCardIconWrapper type={TemplateObjectType.Form}>
          <IconShell icon={<FormTemplateIcon />} />
        </TemplateCardIconWrapper>
      )}
      <Paragraph ellipsis={{ rows: 2 }}>{template.templateName}</Paragraph>

      <MoreActionsWrapper>
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
          role="button"
          tabIndex={0}
        >
          <MoreActionsDropdown
            menu={
              <DropdownMenu
                data-cy="menu"
                data-template-id={template.templateId}
              >
                <DropdownMenu.Item
                  data-cy="menu-items.duplicate"
                  onClick={duplicateHandler}
                  key="duplicate"
                  icon={<Copy16 className="anticon anticon-user" />}
                >
                  <FormattedMessage id="templates.more_actions.duplicate" />
                </DropdownMenu.Item>
                <DropdownMenu.ItemDelete
                  data-cy="menu-items.delete"
                  key="delete"
                  onClick={deleteConfirmation}
                />
              </DropdownMenu>
            }
          />
        </div>
      </MoreActionsWrapper>
    </TemplateCard>
  );
};
